import type { TFunction } from 'i18next';
import {
  formatYearMonthForPicker,
  isDateInFuture,
  isValidNameField,
  splitCvPeriodRange,
  MAX_CV_DESCRIPTION,
  MAX_CV_HEADLINE,
} from '@tabbio/utils';
import type { CVExperience } from '../../../lib/cv-mock-data';

export interface ExperienceErrors {
  role?: string;
  company?: string;
  location?: string;
  start?: string;
  end?: string;
  overview?: string;
}

const MONTH_TOKENS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const YEAR_ONLY = /^\d{4}$/;

// Strict parser for the date-picker's "MMM YYYY" or "YYYY" format. Returns null
// for anything else so callers can show an "invalid date" error instead of
// silently coercing partial garbage (e.g. parseInt('2024abc') === 2024) into
// a real Date.
export function parsePickerDate(value: string): Date | null {
  if (!value) return null;
  const trimmed = value.trim();
  const parts = trimmed.split(/\s+/);
  if (parts.length === 2) {
    const m = MONTH_TOKENS.indexOf(parts[0]);
    if (m < 0 || !YEAR_ONLY.test(parts[1])) return null;
    const y = Number(parts[1]);
    return new Date(y, m, 1);
  }
  if (parts.length === 1 && YEAR_ONLY.test(parts[0])) {
    const y = Number(parts[0]);
    if (y > 1900) return new Date(y, 0, 1);
  }
  return null;
}

export function parsePeriodParts(period: string): { start: string; end: string; current: boolean } {
  return splitCvPeriodRange(period);
}

/**
 * Repair a period string into the exact shape the date picker produces
 * ("Mar 2015 - Dec 2015", "Nov 2023 - Present").
 *
 * Imported CVs and older stored profiles carry period text the picker's strict
 * parser rejects: en/em dashes, "2019 to 2021", "Mar-2015". Left alone those
 * read as one malformed date and every affected entry blocked the onboarding
 * step. Anything genuinely unreadable collapses to "" (an unknown date) rather
 * than being kept as garbage: the same policy `formatDateForPicker` already
 * applies at the import boundary.
 */
export function normalizeExperiencePeriod(period: string): string {
  const { start, end, current } = parsePeriodParts(period);
  return composePeriod(toPickerValue(start), toPickerValue(end), current);
}

/**
 * Apply `normalizeExperiencePeriod` across a list, returning the SAME array
 * reference when nothing needed repairing so callers can skip a state write.
 */
export function normalizeExperiencePeriods<T extends { period: string }>(items: T[]): T[] {
  let changed = false;
  const next = items.map((item) => {
    const period = normalizeExperiencePeriod(item.period);
    if (period === item.period) return item;
    changed = true;
    return { ...item, period };
  });
  return changed ? next : items;
}

/** A picker-formatted date, or "" when the text carries no readable date. */
function toPickerValue(value: string): string {
  const trimmed = value.trim();
  if (!trimmed) return '';
  if (parsePickerDate(trimmed)) return trimmed;
  return formatYearMonthForPicker(trimmed);
}

/**
 * Parse a date the picker's strict format accepts, falling back to the shared
 * loose normalizer so a stored "Mar-2015" or "01/2022" validates instead of
 * being reported as malformed. Returns null only for text carrying no date.
 */
function parseFlexibleDate(value: string): Date | null {
  return parsePickerDate(value) ?? parsePickerDate(formatYearMonthForPicker(value));
}

export function composePeriod(start: string, end: string, current: boolean): string {
  const s = start.trim();
  const e = end.trim();
  if (current) return s ? `${s} - Present` : 'Present';
  if (s && e) return `${s} - ${e}`;
  if (s) return s;
  return e ? `- ${e}` : '';
}

export function currentRoleStartDateError(
  _start: string,
  _t: TFunction,
): string | undefined {
  // An explicitly current role is valid even when its start date is unknown.
  return undefined;
}

export function validateExperience(draft: CVExperience, t: TFunction): ExperienceErrors {
  const errors: ExperienceErrors = {};
  const role = draft.role.trim();
  const meaningfulRoleCharacters = role.match(/[\p{L}\p{N}]/gu)?.length ?? 0;
  if (!role) {
    errors.role = t('cv.edit.roleRequired');
  } else if (meaningfulRoleCharacters < 2) {
    errors.role = t('cv.edit.roleTooShort');
  } else if (draft.role.length > MAX_CV_HEADLINE) {
    errors.role = t('cv.edit.tooLong', { max: MAX_CV_HEADLINE });
  } else if (!isValidNameField(draft.role)) {
    // Issue #34: reject emoji / markup / odd symbols in the short name field.
    errors.role = t('cv.edit.invalidChars');
  }
  if (!draft.company.trim()) {
    errors.company = t('cv.edit.companyRequired');
  } else if (draft.company.length > MAX_CV_HEADLINE) {
    errors.company = t('cv.edit.tooLong', { max: MAX_CV_HEADLINE });
  } else if (!isValidNameField(draft.company)) {
    errors.company = t('cv.edit.invalidChars');
  }
  // Location is NOT required. Issue #180 made it mandatory so a saved position
  // would always say where it was, but plenty of CVs never print a per-role
  // city, so a bulk import stopped at the same gate the dates used to: the user
  // could not reach the next onboarding step without inventing a location for
  // every entry. Same rule as the dates now: absent is fine, present is kept.
  if (draft.overview.length > MAX_CV_DESCRIPTION) {
    errors.overview = t('cv.edit.tooLong', { max: MAX_CV_DESCRIPTION });
  }
  const { start, end, current } = parsePeriodParts(draft.period);
  // A MISSING date is not an error. Issue #183 made the start date required so
  // half-filled entries could not slip through silently, but a 20-role CV
  // import routinely contains roles the source document never dated, and the
  // required rule turned "Continue" into a 20-entry gauntlet with no way past
  // it. Only a date that is genuinely WRONG blocks now: unreadable, in the
  // future, or ending before it starts.
  let startParsed: Date | null = null;
  if (start.trim()) {
    startParsed = parseFlexibleDate(start);
    if (!startParsed) {
      errors.start = t('cv.edit.invalidDate');
    } else if (isDateInFuture(startParsed.toISOString())) {
      errors.start = t('cv.edit.dateInFuture');
    }
  }
  if (!current && end.trim()) {
    const endParsed = parseFlexibleDate(end);
    if (!endParsed) {
      errors.end = t('cv.edit.invalidDate');
    } else if (isDateInFuture(endParsed.toISOString())) {
      errors.end = t('cv.edit.dateInFuture');
    } else if (startParsed && endParsed < startParsed) {
      errors.end = t('cv.edit.startAfterEnd');
    }
  }
  return errors;
}

export function hasAnyExperienceContent(item: CVExperience): boolean {
  return Boolean(
    item.role.trim() ||
      item.company.trim() ||
      item.location.trim() ||
      item.period.trim() ||
      item.overview.trim() ||
      item.bullets.length > 0,
  );
}
